import { useMemo } from 'react'
import type { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router'
import { usePlaceByName } from 'hooks/usePlace'
import type { SupportedDestinations } from 'home/constants'
import { CAROUSEL_IN_VIEW_PARAMS } from 'home/constants'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import { LOOKING_FOR_SOMETHING_ELSE_CONTENT } from 'home/components/LookingForSomethingElse/constants'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferCarouselHeader from 'home/components/OfferCarousels/common/OfferCarouselHeader'
import FullWidthCarouselWithAnalytics from 'components/WithAnalytics/Carousel/FullWidthCarouselWithAnalytics'
import OfferCardLoadingSkeleton from 'components/OfferList/OfferCards/OfferCardLoadingSkeleton'
import ExperienceOfferCard from 'components/OfferList/OfferCards/ExperienceOfferCard'
import OfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import qs from 'qs'
import { take } from 'lib/array/arrayUtils'

const ExperienceOfferCarouselContainer = styled.div`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

const listLimit = 8

interface Props extends RouteComponentProps<{ destination: SupportedDestinations }> {
  title: string
  destination: string
  campaigns?: Array<string>
  offerIds?: Array<string>
}

function ContentfulExperienceOfferCarousel(props: Props) {
  const place = usePlaceByName(props.destination)
  const { campaigns, offerIds } = props
  const hasOverride = !!offerIds?.length

  const [inViewRef, inView] = useInView(CAROUSEL_IN_VIEW_PARAMS)

  const list = useExperienceSearchList({
    placeId: hasOverride ? undefined : place?.id,
    campaigns: hasOverride ? undefined : campaigns,
  })

  const to = useMemo(() => {
    if (place?.id && place?.name) {
      const query = {
        destinationId: place.id,
        destinationName: place.name,
      }
      return `/search/experiences?${qs.stringify(query)}`
    }
    return LOOKING_FOR_SOMETHING_ELSE_CONTENT.experiences.to
  }, [place])

  return (
    <ExperienceOfferCarouselContainer>
      <VerticalSpacer gap={20}>
        <LayoutContainer>
          <VerticalSpacer gap={20}>
            <OfferCarouselHeader allTo="/experiences">
              <Heading variant="heading2" dangerouslySetInnerHTML={{ __html: props.title }} />
            </OfferCarouselHeader>
          </VerticalSpacer>
        </LayoutContainer>
        <FullWidthCarouselWithAnalytics
          snap="start"
          itemsPerArrow={3}
          ref={inViewRef}
        >
          {list.fetching && <LoaderPlayStateContext.Provider value={inView ? 'running' : 'paused'}>
            <OfferCardLoadingSkeleton size="medium" />
            <OfferCardLoadingSkeleton size="medium" />
            <OfferCardLoadingSkeleton size="medium" />
            <OfferCardLoadingSkeleton size="medium" />
          </LoaderPlayStateContext.Provider>}
          {take(hasOverride ? offerIds : list.ids, listLimit).map((id, index) =>
            <ExperienceOfferCard
              key={id}
              id={id}
              index={index}
              lazyLoad
              size="medium"
            />,
          )}
          <OfferCarouselLookingForSomethingElse to={to} />
        </FullWidthCarouselWithAnalytics>
      </VerticalSpacer>
    </ExperienceOfferCarouselContainer>
  )
}

export default withRouter(ContentfulExperienceOfferCarousel)
